import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useRegistrarAdiantamentoExterno, type PendenciaAdiantamentoDoMes } from "@/hooks/use-equipe";
import { paraCentavos } from "@shared/equipe-fechamento";
import { msgDoErro } from "./msg-do-erro";

/**
 * Adiantamento JÁ PAGO fora do sistema (migração 046) — o único caminho de
 * escrita de adiantamento que aceita quinzena FECHADA. Movido de
 * `DialogosFase2.tsx` para a aba "Mensalistas · mês" (Financeiro v3); o
 * diálogo antigo de fechamento continua importando daqui até o Ciclo 2.
 */
export function RegistrarPagoPorForaDialog({
  open, onOpenChange, pendencia, competencia,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  pendencia: PendenciaAdiantamentoDoMes | null;
  competencia: string;
}) {
  const registrar = useRegistrarAdiantamentoExterno();
  const { toast } = useToast();
  const [valor, setValor] = useState("");
  const [pagoEm, setPagoEm] = useState("");
  const [observacao, setObservacao] = useState("");

  useEffect(() => {
    if (!open || !pendencia) return;
    // Valor pré-preenchido com o mesmo `calcValorAdiantamento` do servidor —
    // editável, porque o que foi pago em dinheiro pode não ter sido exatamente
    // a metade.
    setValor(pendencia.valorSugerido);
    setPagoEm("");
    setObservacao("");
  }, [open, pendencia]);

  const salvar = async () => {
    if (!pendencia) return;
    try {
      await registrar.mutateAsync({
        funcionarioId: pendencia.funcionarioId,
        competencia,
        valor,
        pagoEm,
        observacao,
      });
      toast({ title: "Adiantamento registrado — o valor a pagar já caiu." });
      onOpenChange(false);
    } catch (e) {
      toast({ title: msgDoErro(e, "Erro ao registrar o adiantamento"), variant: "destructive" });
    }
  };

  const obsCurta = observacao.trim().length < 5;
  const semData = !/^\d{4}-\d{2}-\d{2}$/.test(pagoEm);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Adiantamento pago por fora</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {pendencia?.apelido ?? pendencia?.nome} · competência {competencia}
          </p>

          <div className="space-y-1">
            <Label className="text-xs">Valor pago</Label>
            <Input type="number" step="0.01" inputMode="decimal" value={valor} onChange={e => setValor(e.target.value)} />
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Data real do pagamento</Label>
            <Input type="date" value={pagoEm} onChange={e => setPagoEm(e.target.value)} />
            <p className="text-[11px] text-muted-foreground">
              A data em que o dinheiro saiu de verdade — pode ser retroativa.
            </p>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Como foi pago</Label>
            <Input
              value={observacao}
              onChange={e => setObservacao(e.target.value)}
              placeholder="pago em dinheiro em 15/08, fora do sistema"
            />
            {obsCurta && (
              <p className="text-[11px] text-erro">
                Obrigatório — sem isso o abatimento vira um número que ninguém explica depois.
              </p>
            )}
          </div>

          <p className="text-[11px] text-muted-foreground">
            Nasce como <strong>PAGO</strong> e abate no fechamento desta competência. Funciona mesmo
            com a quinzena já fechada.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={salvar} disabled={registrar.isPending || obsCurta || semData || paraCentavos(valor) <= 0}>
            {registrar.isPending ? "Registrando..." : "Registrar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
